import React, { useCallback, useEffect, useState } from "react";
import {
    Card,
    Form,
    Input,
    Select,
    Button,
    Table,
    Tag,
    message,
    Row,
    Col,
    Modal,
    Space,
    Popconfirm,
    Tooltip,
} from "antd";
import { SendOutlined, ReloadOutlined, EditOutlined } from "@ant-design/icons";
import { api } from "../api";
import { fmtDate } from "../utils";

const ROLE_OPTIONS = [
    { value: "VOLUNTEER", label: "Volunteers" },
    { value: "GROUP_ADMIN", label: "Group admins" },
    { value: "SUPER_ADMIN", label: "Super admins" },
];

const NotificationsPage = () => {
    const [form] = Form.useForm();
    const [groups, setGroups] = useState([]);
    const [events, setEvents] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const audienceType = Form.useWatch("audienceType", form);
    const linkType = Form.useWatch("linkType", form);

    const groupName = useCallback(
        (id) => groups.find((g) => String(g._id) === String(id))?.name || id,
        [groups]
    );

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.listNotifications();
            setHistory(res.items);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    /* ---------- Automatic notification templates ---------- */
    const [templates, setTemplates] = useState([]);
    const [tplLoading, setTplLoading] = useState(false);
    const [editing, setEditing] = useState(null); // the template being edited
    const [savingTpl, setSavingTpl] = useState(false);
    const [tplForm] = Form.useForm();

    const loadTemplates = useCallback(async () => {
        setTplLoading(true);
        try {
            const res = await api.listNotificationTemplates();
            setTemplates(res.items || []);
        } catch (e) {
            message.error(e.message);
        } finally {
            setTplLoading(false);
        }
    }, []);

    const openEdit = (tpl) => {
        setEditing(tpl);
        tplForm.setFieldsValue({ title: tpl.title, body: tpl.body });
    };

    const saveTemplate = async (values) => {
        setSavingTpl(true);
        try {
            await api.updateNotificationTemplate(editing.key, values);
            message.success("Notification text updated");
            setEditing(null);
            loadTemplates();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSavingTpl(false);
        }
    };

    const resetTemplate = async (key) => {
        try {
            await api.resetNotificationTemplate(key);
            message.success("Reset to the default text");
            loadTemplates();
        } catch (e) {
            message.error(e.message);
        }
    };

    useEffect(() => {
        load();
        loadTemplates();
        api.listGroups()
            .then((r) => setGroups(r.items))
            .catch(() => {});
        api.listEvents()
            .then((r) => setEvents(r.items))
            .catch(() => {});
    }, [load, loadTemplates]);

    const send = async (values) => {
        setSending(true);
        try {
            const res = await api.sendNotification(values);
            message.success(
                `Pushed to ${res.sent} device(s) across ${res.recipientCount} user(s)`
            );
            form.resetFields();
            form.setFieldsValue({ audienceType: "all" });
            load();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSending(false);
        }
    };

    const audienceLabel = (n) => {
        if (n.audienceType === "all") return <Tag color="blue">Everyone</Tag>;
        if (n.audienceType === "role")
            return <Tag color="geekblue">Role: {n.audienceValue}</Tag>;
        return <Tag color="purple">Group: {groupName(n.audienceValue)}</Tag>;
    };

    const columns = [
        { title: "Title", dataIndex: "title", key: "title" },
        {
            title: "Message",
            dataIndex: "body",
            key: "body",
            render: (b) => (
                <span className="admin-cell-sub" style={{ color: "#475569" }}>
                    {(b || "").slice(0, 60)}
                    {b && b.length > 60 ? "…" : ""}
                </span>
            ),
        },
        { title: "Audience", key: "audience", render: (_, n) => audienceLabel(n) },
        {
            title: "Recipients",
            dataIndex: "recipientCount",
            key: "recipientCount",
            width: 100,
        },
        {
            title: "Delivered",
            key: "delivered",
            width: 110,
            render: (_, n) =>
                n.pushDelivered ? (
                    <Tag color="green">{n.sentCount || 0} sent</Tag>
                ) : (
                    <Tag>not pushed</Tag>
                ),
        },
        {
            title: "Sent",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 170,
            render: (d) => fmtDate(d, true),
        },
    ];

    return (
        <div>
            <h1 className="admin-page-title">Notifications</h1>
            <Row gutter={[24, 24]}>
                <Col xs={24} lg={9}>
                    <Card title="Send a notification" variant="borderless">
                        <Form
                            form={form}
                            layout="vertical"
                            initialValues={{ audienceType: "all" }}
                            onFinish={send}
                            requiredMark={false}
                        >
                            <Form.Item
                                name="title"
                                label="Title"
                                rules={[{ required: true, message: "Title is required" }]}
                            >
                                <Input placeholder="e.g. New event this weekend" />
                            </Form.Item>
                            <Form.Item
                                name="body"
                                label="Message"
                                rules={[{ required: true, message: "Message is required" }]}
                            >
                                <Input.TextArea rows={4} placeholder="Write your message…" />
                            </Form.Item>
                            <Form.Item name="audienceType" label="Send to">
                                <Select
                                    options={[
                                        { value: "all", label: "Everyone" },
                                        { value: "role", label: "A specific role" },
                                        { value: "group", label: "A specific group" },
                                    ]}
                                />
                            </Form.Item>

                            {audienceType === "role" && (
                                <Form.Item
                                    name="audienceValue"
                                    label="Role"
                                    rules={[{ required: true, message: "Pick a role" }]}
                                >
                                    <Select options={ROLE_OPTIONS} />
                                </Form.Item>
                            )}
                            {audienceType === "group" && (
                                <Form.Item
                                    name="audienceValue"
                                    label="Group"
                                    rules={[{ required: true, message: "Pick a group" }]}
                                >
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder="Select a group"
                                        options={groups.map((g) => ({
                                            value: g._id,
                                            label: g.name,
                                        }))}
                                    />
                                </Form.Item>
                            )}

                            <Form.Item
                                name="linkType"
                                label="Open on tap (optional)"
                            >
                                <Select
                                    allowClear
                                    placeholder="Just open the app"
                                    options={[{ value: "event", label: "An event" }]}
                                />
                            </Form.Item>
                            {linkType === "event" && (
                                <Form.Item
                                    name="linkId"
                                    label="Event"
                                    rules={[{ required: true, message: "Pick an event" }]}
                                >
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder="Select an event"
                                        options={events.map((e) => ({
                                            value: e._id,
                                            label: e.title,
                                        }))}
                                    />
                                </Form.Item>
                            )}

                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SendOutlined />}
                                loading={sending}
                                block
                            >
                                Send notification
                            </Button>
                        </Form>
                    </Card>
                </Col>

                <Col xs={24} lg={15}>
                    <Card
                        title="History"
                        variant="borderless"
                        extra={
                            <Button
                                size="small"
                                icon={<ReloadOutlined />}
                                loading={loading}
                                onClick={load}
                            >
                                Refresh
                            </Button>
                        }
                    >
                        <Table
                            rowKey="_id"
                            size="small"
                            loading={loading}
                            columns={columns}
                            dataSource={history}
                            scroll={{ x: "max-content" }}
                            pagination={{ pageSize: 8 }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Automatic notifications — what the system sends on its own. */}
            <Card
                title="Automatic notifications"
                variant="borderless"
                style={{ marginTop: 24 }}
                extra={
                    <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        loading={tplLoading}
                        onClick={loadTemplates}
                    >
                        Refresh
                    </Button>
                }
            >
                <p style={{ color: "#64748b", marginTop: 0 }}>
                    These are sent automatically by the system (no one presses “send”).
                    Edit the wording here — <code>{"{{placeholders}}"}</code> are filled in
                    with real values when the notification goes out.
                </p>
                <Table
                    rowKey="key"
                    size="small"
                    loading={tplLoading}
                    dataSource={templates}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                    columns={[
                        {
                            title: "When it's sent",
                            key: "label",
                            render: (_, t) => (
                                <div>
                                    <div style={{ fontWeight: 600 }}>{t.label}</div>
                                    <div style={{ color: "#64748b", fontSize: 12 }}>
                                        {t.description}
                                    </div>
                                </div>
                            ),
                        },
                        {
                            title: "Title",
                            dataIndex: "title",
                            key: "title",
                            width: 200,
                        },
                        {
                            title: "Message",
                            dataIndex: "body",
                            key: "body",
                            width: 320,
                        },
                        {
                            title: "Placeholders",
                            key: "placeholders",
                            width: 170,
                            render: (_, t) => (
                                <Space size={4} wrap>
                                    {t.placeholders.map((p) => (
                                        <Tag key={p}>{`{{${p}}}`}</Tag>
                                    ))}
                                </Space>
                            ),
                        },
                        {
                            title: "Status",
                            key: "status",
                            width: 100,
                            render: (_, t) =>
                                t.isCustomised ? (
                                    <Tooltip title={fmtDate(t.updatedAt, true)}>
                                        <Tag color="blue">Custom</Tag>
                                    </Tooltip>
                                ) : (
                                    <Tag>Default</Tag>
                                ),
                        },
                        {
                            title: "",
                            key: "actions",
                            width: 150,
                            align: "right",
                            render: (_, t) => (
                                <Space>
                                    <Button
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={() => openEdit(t)}
                                    >
                                        Edit
                                    </Button>
                                    {t.isCustomised && (
                                        <Popconfirm
                                            title="Restore the default wording?"
                                            okText="Reset"
                                            onConfirm={() => resetTemplate(t.key)}
                                        >
                                            <Button size="small" danger>
                                                Reset
                                            </Button>
                                        </Popconfirm>
                                    )}
                                </Space>
                            ),
                        },
                    ]}
                />
            </Card>

            <Modal
                open={!!editing}
                title={editing ? `Edit — ${editing.label}` : ""}
                onCancel={() => setEditing(null)}
                onOk={() => tplForm.submit()}
                confirmLoading={savingTpl}
                okText="Save"
                destroyOnHidden
            >
                {editing && (
                    <>
                        <p style={{ color: "#64748b", marginTop: 0 }}>
                            {editing.description}
                        </p>
                        <p style={{ marginBottom: 16 }}>
                            Available placeholders:{" "}
                            <Space size={4} wrap>
                                {editing.placeholders.map((p) => (
                                    <Tag key={p}>{`{{${p}}}`}</Tag>
                                ))}
                            </Space>
                        </p>
                        <Form
                            form={tplForm}
                            layout="vertical"
                            onFinish={saveTemplate}
                            requiredMark={false}
                        >
                            <Form.Item
                                name="title"
                                label="Title"
                                rules={[{ required: true, message: "Title is required" }]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="body"
                                label="Message"
                                rules={[{ required: true, message: "Message is required" }]}
                            >
                                <Input.TextArea rows={3} />
                            </Form.Item>
                        </Form>
                        <div style={{ color: "#94a3b8", fontSize: 12 }}>
                            Default: “{editing.defaultTitle}” — {editing.defaultBody}
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default NotificationsPage;
